<template>
  <div class="px-3 pt-2 h-full overflow-auto pb-5 text-[13px] leading-normal text-dark-text">
    <div v-if="isAgent" class="text-12 text-gray-500 py-3">
      This transformation runs as an agent tool. The agent supplies the input data, the pipeline steps, and the output at runtime — nothing to configure here besides the tool description and policy.
    </div>
    <template v-if="!isAgent">
      <FormInput v-model="title" name="title" label="Transformation Pipeline Title" class="w-full" @update:modelValue="onPayloadChange" />
      <p v-if="submitted && !title" class="mt-1 text-[11px] text-alert font-medium">Title is required.</p>

      <!-- ─── Input Variable ─── -->
      <VariableSelect label="Input Variable" v-model="inputVariable" class="w-full mt-3" system :filter-data-type="['DataTable']" @update:modelValue="onPayloadChange" />
      <p v-if="submitted && !inputVariable" class="mt-1 text-[11px] text-alert font-medium">Input variable is required.</p>

      <!-- ─── Steps ─── -->
      <div class="mt-3 px-4 py-3 border border-stroke rounded-t-md">
        <div class="flex items-center justify-between gap-3 mb-2 flex-wrap">
          <label class="font-semibold text-xs uppercase tracking-wide text-gray-700 font-500">Pipeline Steps</label>
          <button type="button" class="inline-flex items-center gap-1 px-3 py-1 text-xs font-semibold rounded-md bg-secondary text-on-secondary hover:opacity-90 transition-opacity" @click="addStep">
            <svg viewBox="0 0 20 20" fill="currentColor" class="w-4 h-4">
              <path fill-rule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clip-rule="evenodd" />
            </svg>
            Add Step
          </button>
        </div>

        <p v-if="submitted && steps.length === 0" class="mt-1 text-[11px] text-alert font-medium">At least one step is required.</p>

        <!-- Empty state -->
        <div v-if="steps.length === 0" class="flex flex-col items-center py-6 px-4 text-gray-500 text-center text-[13px]">
          <svg class="w-8 h-8 mb-2 opacity-40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
          </svg>
          <p>No steps yet. Click <strong>Add Step</strong> to begin building your pipeline.</p>
        </div>

        <!-- Step list -->
        <transition-group name="dtb-step-anim" tag="div" class="flex flex-col gap-2">
          <div v-for="(step, idx) in steps" :key="step.id" class="border border-stroke rounded-lg bg-white overflow-hidden">
            <!-- Step header bar -->
            <div class="flex items-center gap-2 px-2 py-2 bg-surface-dark border-b border-stroke flex-wrap">
              <span class="flex items-center justify-center w-6 h-6 rounded-full bg-primary text-on-primary text-[11px] font-bold shrink-0">{{ idx + 1 }}</span>

              <select v-model="step.step" class="dtb-select flex-1 min-w-[140px]" @change="onStepTypeChange(idx)">
                <option value="" disabled>Select step…</option>
                <optgroup v-for="group in stepOptionGroups" :key="group.label" :label="group.label">
                  <option v-for="s in group.options" :key="s.value" :value="s.value">{{ s.label }}</option>
                </optgroup>
              </select>

              <div class="flex gap-0.5 ml-auto shrink-0">
                <button type="button" class="dtb-icon-btn" title="Move up" :disabled="idx === 0" @click="moveStep(idx, -1)">
                  <svg viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clip-rule="evenodd" />
                  </svg>
                </button>
                <button type="button" class="dtb-icon-btn" title="Move down" :disabled="idx === steps.length - 1" @click="moveStep(idx, 1)">
                  <svg viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
                  </svg>
                </button>
                <button type="button" class="dtb-icon-btn" title="Duplicate step" @click="duplicateStep(idx)">
                  <svg viewBox="0 0 20 20" fill="currentColor">
                    <path d="M7 9a2 2 0 012-2h6a2 2 0 012 2v6a2 2 0 01-2 2H9a2 2 0 01-2-2V9z" />
                    <path d="M5 3a2 2 0 00-2 2v6a2 2 0 002 2V5h8a2 2 0 00-2-2H5z" />
                  </svg>
                </button>
                <button type="button" class="dtb-icon-btn hover:!bg-alert/10 hover:!text-alert" title="Remove step" @click="removeStep(idx)">
                  <svg viewBox="0 0 20 20" fill="currentColor">
                    <path
                      fill-rule="evenodd"
                      d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011
                     2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                      clip-rule="evenodd"
                    />
                  </svg>
                </button>
              </div>
            </div>

            <!-- Step description -->
            <p v-if="step.step && stepRegistry[step.step]" class="m-0 px-3 py-2 text-[11px] text-gray-500 italic border-b border-stroke/50">
              {{ stepRegistry[step.step].description }}
            </p>

            <!-- Validation: step type required -->
            <p v-if="submitted && !step.step" class="px-3 py-2 text-[11px] text-alert font-medium">Please select a step type.</p>

            <!-- ─── Dynamic config fields ─── -->
            <div v-if="step.step" class="p-3">
              <!-- FILTER -->
              <template v-if="step.step === 'Filter'">
                <div class="flex gap-2 flex-wrap sm:flex-nowrap">
                  <div class="flex-1 min-w-0">
                    <label class="dtb-field-label">Column Name <span class="text-alert">*</span></label>
                    <input v-model="step.config.columnName" class="dtb-input" placeholder="e.g. Status" @input="onPayloadChange" />
                    <p v-if="submitted && !step.config.columnName" class="dtb-error">Required.</p>
                  </div>
                  <div class="flex-1 min-w-0">
                    <label class="dtb-field-label">Operator <span class="text-alert">*</span></label>
                    <select v-model="step.config.operation" class="dtb-select" @change="onPayloadChange">
                      <option value="" disabled>Select…</option>
                      <option v-for="op in operatorOptions" :key="op.value" :value="op.value">{{ op.label }}</option>
                    </select>
                    <p v-if="submitted && !step.config.operation" class="dtb-error">Required.</p>
                  </div>
                  <div v-if="!['isEmpty', 'isNotEmpty'].includes(step.config.operation)" class="flex-1 min-w-0">
                    <label class="dtb-field-label">Value</label>
                    <input v-model="step.config.value" class="dtb-input" placeholder="Comparison value" @input="onPayloadChange" />
                  </div>
                </div>
              </template>

              <!-- SELECT COLUMNS -->
              <template v-if="step.step === 'SelectColumns'">
                <div class="mb-2">
                  <label class="dtb-field-label">Columns <span class="text-alert">*</span></label>
                  <tag-input
                    :tags="step.config.columns"
                    :input-value="step.colInput"
                    @update:inputValue="(val) => (step.colInput = val)"
                    @add="addTag(step, 'columns')"
                    @remove="(ci) => removeTag(step.config.columns, ci)"
                  />
                  <p v-if="submitted && (!step.config.columns || step.config.columns.length === 0)" class="dtb-error">At least one column required.</p>
                </div>
              </template>

              <!-- UPDATE COLUMN -->
              <template v-if="step.step === 'UpdateColumn'">
                <div class="flex gap-2 mb-2 flex-col sm:flex-row">
                  <div class="flex-1 min-w-0">
                    <label class="dtb-field-label">Column Name <span class="text-alert">*</span></label>
                    <input v-model="step.config.columnName" class="dtb-input" placeholder="e.g. Status" @input="onPayloadChange" />
                    <p v-if="submitted && !step.config.columnName" class="dtb-error">Required.</p>
                  </div>
                  <div class="flex-1 min-w-0">
                    <label class="dtb-field-label">Transform <span class="text-alert">*</span></label>
                    <select v-model="step.config.transform" class="dtb-select" @change="onTransformChange(step)">
                      <option value="" disabled>Select…</option>
                      <optgroup v-for="group in transformOptionGroups" :key="group.label" :label="group.label">
                        <option v-for="t in group.options" :key="t.value" :value="t.value">{{ t.label }}</option>
                      </optgroup>
                    </select>
                    <p v-if="submitted && !step.config.transform" class="dtb-error">Required.</p>
                  </div>
                </div>
                <transform-fields
                  v-if="step.config.transform"
                  :step="step"
                  :submitted="submitted"
                  :operator-options="operatorOptions"
                  :show-source-column="false"
                  @update:step="(updatedStep) => Object.assign(step, updatedStep)"
                  @change="onPayloadChange"
                  @add-tag="addTag"
                  @add-tag-custom="addTagCustom"
                  @remove-tag="removeTag"
                />
              </template>

              <!-- ADD COLUMN -->
              <template v-if="step.step === 'AddColumn'">
                <div class="flex gap-2 mb-2 flex-col sm:flex-row">
                  <div class="flex-1 min-w-0">
                    <label class="dtb-field-label">New Column Name <span class="text-alert">*</span></label>
                    <input v-model="step.config.newColumnName" class="dtb-input" placeholder="e.g. Product Name" @input="onPayloadChange" />
                    <p v-if="submitted && !step.config.newColumnName" class="dtb-error">Required.</p>
                  </div>
                  <div class="flex-1 min-w-0">
                    <label class="dtb-field-label">Transform <span class="text-alert">*</span></label>
                    <select v-model="step.config.transform" class="dtb-select" @change="onTransformChange(step)">
                      <option value="" disabled>Select…</option>
                      <optgroup v-for="group in transformOptionGroups" :key="group.label" :label="group.label">
                        <option v-for="t in group.options" :key="t.value" :value="t.value">{{ t.label }}</option>
                      </optgroup>
                    </select>
                    <p v-if="submitted && !step.config.transform" class="dtb-error">Required.</p>
                  </div>
                </div>
                <!-- Source column (for transforms that need it) -->
                <div v-if="step.config.transform && needsColumnName(step.config.transform)" class="mb-2">
                  <label class="dtb-field-label">Source Column <span class="text-alert">*</span></label>
                  <input v-model="step.config.columnName" class="dtb-input" placeholder="Existing column to read from" @input="onPayloadChange" />
                  <p v-if="submitted && !step.config.columnName" class="dtb-error">Required.</p>
                </div>
                <transform-fields
                  v-if="step.config.transform"
                  :step="step"
                  :submitted="submitted"
                  :operator-options="operatorOptions"
                  :show-source-column="false"
                  @update:step="(updatedStep) => Object.assign(step, updatedStep)"
                  @change="onPayloadChange"
                  @add-tag="addTag"
                  @add-tag-custom="addTagCustom"
                  @remove-tag="removeTag"
                />
              </template>

              <!-- REMOVE COLUMN -->
              <template v-if="step.step === 'RemoveColumn'">
                <div class="mb-2">
                  <label class="dtb-field-label">Columns to Remove <span class="text-alert">*</span></label>
                  <tag-input
                    :tags="step.config.columns"
                    :input-value="step.colInput"
                    @update:inputValue="(val) => (step.colInput = val)"
                    @add="addTag(step, 'columns')"
                    @remove="(ci) => removeTag(step.config.columns, ci)"
                  />
                  <p v-if="submitted && (!step.config.columns || step.config.columns.length === 0)" class="dtb-error">At least one column required.</p>
                </div>
              </template>

              <!-- RENAME COLUMN -->
              <template v-if="step.step === 'RenameColumn'">
                <div class="flex gap-2 flex-col sm:flex-row">
                  <div class="flex-1 min-w-0">
                    <label class="dtb-field-label">Current Name <span class="text-alert">*</span></label>
                    <input v-model="step.config.columnName" class="dtb-input" placeholder="Old column name" @input="onPayloadChange" />
                    <p v-if="submitted && !step.config.columnName" class="dtb-error">Required.</p>
                  </div>
                  <div class="flex-1 min-w-0">
                    <label class="dtb-field-label">New Name <span class="text-alert">*</span></label>
                    <input v-model="step.config.newColumnName" class="dtb-input" placeholder="New column name" @input="onPayloadChange" />
                    <p v-if="submitted && !step.config.newColumnName" class="dtb-error">Required.</p>
                  </div>
                </div>
              </template>

              <!-- RENAME COLUMNS (batch) -->
              <template v-if="step.step === 'RenameColumns'">
                <div class="mb-2">
                  <label class="dtb-field-label">Column Mappings <span class="text-alert">*</span></label>
                  <div v-for="(entry, ri) in step.renameEntries" :key="ri" class="flex items-center gap-2 mb-2">
                    <input v-model="entry.oldName" class="dtb-input flex-1 min-w-0" placeholder="Old name" @input="syncRenameMapping(step)" />
                    <span class="shrink-0 text-sm text-gray-500 font-semibold">→</span>
                    <input v-model="entry.newName" class="dtb-input flex-1 min-w-0" placeholder="New name" @input="syncRenameMapping(step)" />
                    <button
                      type="button"
                      class="dtb-icon-btn hover:!bg-alert/10 hover:!text-alert text-base w-6 h-6"
                      @click="
                        step.renameEntries.splice(ri, 1);
                        syncRenameMapping(step);
                      "
                    >
                      &times;
                    </button>
                  </div>
                  <button type="button" class="dtb-btn-sm" @click="step.renameEntries.push({ oldName: '', newName: '' })">+ Add mapping</button>
                </div>
              </template>

              <!-- GROUP BY AND COUNT -->
              <template v-if="step.step === 'GroupByAndCount'">
                <div class="flex gap-2 mb-2 flex-col sm:flex-row">
                  <div class="flex-1 min-w-0">
                    <label class="dtb-field-label">Group Columns <span class="text-alert">*</span></label>
                    <tag-input
                      :tags="step.config.groupColumns"
                      :input-value="step.colInput"
                      @update:inputValue="(val) => (step.colInput = val)"
                      @add="addTag(step, 'groupColumns')"
                      @remove="(ci) => removeTag(step.config.groupColumns, ci)"
                    />
                    <p v-if="submitted && (!step.config.groupColumns || step.config.groupColumns.length === 0)" class="dtb-error">At least one column required.</p>
                  </div>
                  <div class="flex-1 min-w-0">
                    <label class="dtb-field-label">Count Column Name <span class="text-alert">*</span></label>
                    <input v-model="step.config.countColumn" class="dtb-input" placeholder="e.g. Count" @input="onPayloadChange" />
                    <p v-if="submitted && !step.config.countColumn" class="dtb-error">Required.</p>
                  </div>
                </div>
              </template>

              <!-- GROUP BY AND SUM -->
              <template v-if="step.step === 'GroupByAndSum'">
                <div class="mb-2">
                  <label class="dtb-field-label">Group Columns <span class="text-alert">*</span></label>
                  <tag-input
                    :tags="step.config.groupColumns"
                    :input-value="step.colInput"
                    @update:inputValue="(val) => (step.colInput = val)"
                    @add="addTag(step, 'groupColumns')"
                    @remove="(ci) => removeTag(step.config.groupColumns, ci)"
                  />
                  <p v-if="submitted && (!step.config.groupColumns || step.config.groupColumns.length === 0)" class="dtb-error">At least one column required.</p>
                </div>
                <div class="mb-2">
                  <label class="dtb-field-label">Sum Columns <span class="text-alert">*</span></label>
                  <tag-input
                    :tags="step.config.sumColumns"
                    :input-value="step.sumInput"
                    @update:inputValue="(val) => (step.sumInput = val)"
                    @add="addTagCustom(step, 'sumInput', 'sumColumns')"
                    @remove="(ci) => removeTag(step.config.sumColumns, ci)"
                  />
                  <p v-if="submitted && (!step.config.sumColumns || step.config.sumColumns.length === 0)" class="dtb-error">At least one column required.</p>
                </div>
              </template>

              <!-- GROUP BY AND AVERAGE -->
              <template v-if="step.step === 'GroupByAndAverage'">
                <div class="mb-2">
                  <label class="dtb-field-label">Group Columns <span class="text-alert">*</span></label>
                  <tag-input
                    :tags="step.config.groupColumns"
                    :input-value="step.colInput"
                    @update:inputValue="(val) => (step.colInput = val)"
                    @add="addTag(step, 'groupColumns')"
                    @remove="(ci) => removeTag(step.config.groupColumns, ci)"
                  />
                  <p v-if="submitted && (!step.config.groupColumns || step.config.groupColumns.length === 0)" class="dtb-error">At least one column required.</p>
                </div>
                <div class="mb-2">
                  <label class="dtb-field-label">Average Columns <span class="text-alert">*</span></label>
                  <tag-input
                    :tags="step.config.averageColumns"
                    :input-value="step.avgInput"
                    @update:inputValue="(val) => (step.avgInput = val)"
                    @add="addTagCustom(step, 'avgInput', 'averageColumns')"
                    @remove="(ci) => removeTag(step.config.averageColumns, ci)"
                  />
                  <p v-if="submitted && (!step.config.averageColumns || step.config.averageColumns.length === 0)" class="dtb-error">At least one column required.</p>
                </div>
              </template>

              <!-- GROUP BY AND MIN -->
              <template v-if="step.step === 'GroupByAndMin'">
                <div class="mb-2">
                  <label class="dtb-field-label">Group Columns <span class="text-alert">*</span></label>
                  <tag-input
                    :tags="step.config.groupColumns"
                    :input-value="step.colInput"
                    @update:inputValue="(val) => (step.colInput = val)"
                    @add="addTag(step, 'groupColumns')"
                    @remove="(ci) => removeTag(step.config.groupColumns, ci)"
                  />
                </div>
                <div class="mb-2">
                  <label class="dtb-field-label">Min Columns <span class="text-alert">*</span></label>
                  <tag-input
                    :tags="step.config.minColumns"
                    :input-value="step.minInput"
                    @update:inputValue="(val) => (step.minInput = val)"
                    @add="addTagCustom(step, 'minInput', 'minColumns')"
                    @remove="(ci) => removeTag(step.config.minColumns, ci)"
                  />
                </div>
              </template>

              <!-- GROUP BY AND MAX -->
              <template v-if="step.step === 'GroupByAndMax'">
                <div class="mb-2">
                  <label class="dtb-field-label">Group Columns <span class="text-alert">*</span></label>
                  <tag-input
                    :tags="step.config.groupColumns"
                    :input-value="step.colInput"
                    @update:inputValue="(val) => (step.colInput = val)"
                    @add="addTag(step, 'groupColumns')"
                    @remove="(ci) => removeTag(step.config.groupColumns, ci)"
                  />
                </div>
                <div class="mb-2">
                  <label class="dtb-field-label">Max Columns <span class="text-alert">*</span></label>
                  <tag-input
                    :tags="step.config.maxColumns"
                    :input-value="step.maxInput"
                    @update:inputValue="(val) => (step.maxInput = val)"
                    @add="addTagCustom(step, 'maxInput', 'maxColumns')"
                    @remove="(ci) => removeTag(step.config.maxColumns, ci)"
                  />
                </div>
              </template>

              <!-- SORT BY -->
              <template v-if="step.step === 'SortBy'">
                <div class="mb-2">
                  <label class="dtb-field-label">Sort Columns <span class="text-alert">*</span></label>
                  <div v-for="(sc, si) in step.config.sortColumns" :key="si" class="flex items-center gap-2 mb-2 flex-wrap sm:flex-nowrap">
                    <input v-model="sc.columnName" class="dtb-input flex-1 min-w-0" placeholder="Column name" @input="onPayloadChange" />
                    <select v-model="sc.direction" class="dtb-select w-full sm:w-[120px] shrink-0" @change="onPayloadChange">
                      <option value="asc">Ascending</option>
                      <option value="desc">Descending</option>
                    </select>
                    <button
                      type="button"
                      class="dtb-icon-btn hover:!bg-alert/10 hover:!text-alert"
                      @click="
                        step.config.sortColumns.splice(si, 1);
                        onPayloadChange();
                      "
                    >
                      &times;
                    </button>
                  </div>
                  <button type="button" class="dtb-btn-sm" @click="step.config.sortColumns.push({ columnName: '', direction: 'asc' })">+ Add sort column</button>
                  <p v-if="submitted && (!step.config.sortColumns || step.config.sortColumns.length === 0)" class="dtb-error">At least one sort column required.</p>
                </div>
              </template>

              <!-- DEDUPLICATE -->
              <template v-if="step.step === 'Deduplicate'">
                <div class="mb-2">
                  <label class="dtb-field-label">Columns for Uniqueness <span class="text-alert">*</span></label>
                  <tag-input
                    :tags="step.config.columns"
                    :input-value="step.colInput"
                    @update:inputValue="(val) => (step.colInput = val)"
                    @add="addTag(step, 'columns')"
                    @remove="(ci) => removeTag(step.config.columns, ci)"
                  />
                  <p v-if="submitted && (!step.config.columns || step.config.columns.length === 0)" class="dtb-error">At least one column required.</p>
                </div>
              </template>

              <!-- LIMIT -->
              <template v-if="step.step === 'Limit'">
                <div class="mb-2">
                  <label class="dtb-field-label">Row Count <span class="text-alert">*</span></label>
                  <input v-model.number="step.config.count" type="number" min="1" class="dtb-input" placeholder="e.g. 100" @input="onPayloadChange" />
                  <p v-if="submitted && (!step.config.count || step.config.count < 1)" class="dtb-error">Must be at least 1.</p>
                </div>
              </template>

              <!-- FILL MISSING -->
              <template v-if="step.step === 'FillMissing'">
                <div class="flex gap-2 flex-col sm:flex-row">
                  <div class="flex-1 min-w-0">
                    <label class="dtb-field-label">Column Name <span class="text-alert">*</span></label>
                    <input v-model="step.config.columnName" class="dtb-input" placeholder="e.g. Region" @input="onPayloadChange" />
                    <p v-if="submitted && !step.config.columnName" class="dtb-error">Required.</p>
                  </div>
                  <div class="flex-1 min-w-0">
                    <label class="dtb-field-label">Fill Value <span class="text-alert">*</span></label>
                    <input v-model="step.config.fillValue" class="dtb-input" placeholder="e.g. Unknown" @input="onPayloadChange" />
                    <p v-if="submitted && (step.config.fillValue === '' || step.config.fillValue === undefined)" class="dtb-error">Required.</p>
                  </div>
                </div>
              </template>

              <!-- PIVOT -->
              <template v-if="step.step === 'Pivot'">
                <div class="mb-2">
                  <label class="dtb-field-label">Group Columns <span class="text-alert">*</span></label>
                  <tag-input
                    :tags="step.config.groupColumns"
                    :input-value="step.colInput"
                    @update:inputValue="(val) => (step.colInput = val)"
                    @add="addTag(step, 'groupColumns')"
                    @remove="(ci) => removeTag(step.config.groupColumns, ci)"
                  />
                </div>
                <div class="flex gap-2 mb-2 flex-col sm:flex-row">
                  <div class="flex-1 min-w-0">
                    <label class="dtb-field-label">Pivot Column <span class="text-alert">*</span></label>
                    <input v-model="step.config.pivotColumn" class="dtb-input" placeholder="Column whose values become headers" @input="onPayloadChange" />
                    <p v-if="submitted && !step.config.pivotColumn" class="dtb-error">Required.</p>
                  </div>
                  <div class="flex-1 min-w-0">
                    <label class="dtb-field-label">Value Column <span class="text-alert">*</span></label>
                    <input v-model="step.config.valueColumn" class="dtb-input" placeholder="Column for cell values" @input="onPayloadChange" />
                    <p v-if="submitted && !step.config.valueColumn" class="dtb-error">Required.</p>
                  </div>
                </div>
                <div class="mb-2">
                  <label class="dtb-field-label">Aggregation</label>
                  <select v-model="step.config.aggregation" class="dtb-select" @change="onPayloadChange">
                    <option value="sum">Sum</option>
                    <option value="count">Count</option>
                    <option value="average">Average</option>
                    <option value="min">Min</option>
                    <option value="max">Max</option>
                  </select>
                </div>
              </template>

              <!-- UNPIVOT -->
              <template v-if="step.step === 'Unpivot'">
                <div class="mb-2">
                  <label class="dtb-field-label">ID Columns (keep as-is) <span class="text-alert">*</span></label>
                  <tag-input
                    :tags="step.config.idColumns"
                    :input-value="step.colInput"
                    @update:inputValue="(val) => (step.colInput = val)"
                    @add="addTag(step, 'idColumns')"
                    @remove="(ci) => removeTag(step.config.idColumns, ci)"
                  />
                </div>
                <div class="mb-2">
                  <label class="dtb-field-label">Unpivot Columns <span class="text-alert">*</span></label>
                  <tag-input
                    :tags="step.config.unpivotColumns"
                    :input-value="step.unpivotInput"
                    @update:inputValue="(val) => (step.unpivotInput = val)"
                    @add="addTagCustom(step, 'unpivotInput', 'unpivotColumns')"
                    @remove="(ci) => removeTag(step.config.unpivotColumns, ci)"
                  />
                </div>
                <div class="flex gap-2 flex-col sm:flex-row">
                  <div class="flex-1 min-w-0">
                    <label class="dtb-field-label">Key Column Name</label>
                    <input v-model="step.config.keyColumn" class="dtb-input" placeholder="Variable" @input="onPayloadChange" />
                  </div>
                  <div class="flex-1 min-w-0">
                    <label class="dtb-field-label">Value Column Name</label>
                    <input v-model="step.config.valueColumn" class="dtb-input" placeholder="Value" @input="onPayloadChange" />
                  </div>
                </div>
              </template>

              <!-- ═══ NEW STEPS ═══ -->

              <!-- AVERAGE (all numeric columns) -->
              <template v-if="step.step === 'Average'">
                <p class="text-[11px] text-gray-500 italic">This step calculates the average for all numeric columns. No configuration needed.</p>
                <div class="mb-2">
                  <label class="dtb-field-label">Output Column Suffix</label>
                  <input v-model="step.config.suffix" class="dtb-input" placeholder="e.g. _avg (default: _avg)" @input="onPayloadChange" />
                  <p class="dtb-hint">Appended to each numeric column name in the result, e.g. Revenue_avg.</p>
                </div>
              </template>

              <!-- SUMMARIZE (totals for all numeric columns) -->
              <template v-if="step.step === 'Summarize'">
                <p class="text-[11px] text-gray-500 italic">Calculates totals (sum) for all numeric columns and returns a single summary row.</p>
                <div class="mb-2">
                  <label class="dtb-field-label">Label Column</label>
                  <input v-model="step.config.labelColumn" class="dtb-input" placeholder="e.g. Category (optional)" @input="onPayloadChange" />
                  <p class="dtb-hint">If set, a column with this name will contain the label value below.</p>
                </div>
                <div class="mb-2">
                  <label class="dtb-field-label">Label Value</label>
                  <input v-model="step.config.labelValue" class="dtb-input" placeholder="e.g. Total" @input="onPayloadChange" />
                </div>
              </template>

              <!-- FORMAT -->
              <template v-if="step.step === 'Format'">
                <div class="flex gap-2 mb-2 flex-col sm:flex-row">
                  <div class="flex-1 min-w-0">
                    <label class="dtb-field-label">Column Name <span class="text-alert">*</span></label>
                    <input v-model="step.config.columnName" class="dtb-input" placeholder="e.g. Revenue" @input="onPayloadChange" />
                    <p v-if="submitted && !step.config.columnName" class="dtb-error">Required.</p>
                  </div>
                  <div class="flex-1 min-w-0">
                    <label class="dtb-field-label">Format Type <span class="text-alert">*</span></label>
                    <select v-model="step.config.formatType" class="dtb-select" @change="onPayloadChange">
                      <option value="" disabled>Select…</option>
                      <option value="number">Number (e.g. 1,234.56)</option>
                      <option value="currency">Currency (e.g. $1,234.56)</option>
                      <option value="percent">Percent (e.g. 45.2%)</option>
                      <option value="date">Date (e.g. 2024-01-15)</option>
                      <option value="custom">Custom pattern</option>
                    </select>
                    <p v-if="submitted && !step.config.formatType" class="dtb-error">Required.</p>
                  </div>
                </div>
                <div v-if="step.config.formatType === 'number' || step.config.formatType === 'currency'" class="flex gap-2 mb-2 flex-col sm:flex-row">
                  <div class="flex-1 min-w-0">
                    <label class="dtb-field-label">Decimal Places</label>
                    <input v-model.number="step.config.decimals" type="number" min="0" class="dtb-input" @input="onPayloadChange" />
                  </div>
                  <div v-if="step.config.formatType === 'currency'" class="flex-1 min-w-0">
                    <label class="dtb-field-label">Currency Symbol</label>
                    <input v-model="step.config.currencySymbol" class="dtb-input" placeholder="$ (default)" @input="onPayloadChange" />
                  </div>
                  <div class="flex-1 min-w-0">
                    <label class="dtb-field-label">Thousands Separator</label>
                    <select v-model="step.config.thousandsSeparator" class="dtb-select" @change="onPayloadChange">
                      <option value=",">, (comma)</option>
                      <option value=".">. (dot)</option>
                      <option value=" ">space</option>
                      <option value="">none</option>
                    </select>
                  </div>
                </div>
                <div v-if="step.config.formatType === 'percent'" class="mb-2">
                  <label class="dtb-field-label">Decimal Places</label>
                  <input v-model.number="step.config.decimals" type="number" min="0" class="dtb-input" @input="onPayloadChange" />
                </div>
                <div v-if="step.config.formatType === 'date'" class="mb-2">
                  <label class="dtb-field-label">Date Format <span class="text-alert">*</span></label>
                  <select v-model="step.config.dateFormat" class="dtb-select" @change="onPayloadChange">
                    <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                    <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                    <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                    <option value="DD MMM YYYY">DD MMM YYYY (e.g. 15 Jan 2024)</option>
                    <option value="MMM YYYY">MMM YYYY (e.g. Jan 2024)</option>
                    <option value="YYYY-MM">YYYY-MM</option>
                  </select>
                </div>
                <div v-if="step.config.formatType === 'custom'" class="mb-2">
                  <label class="dtb-field-label">Custom Pattern <span class="text-alert">*</span></label>
                  <input v-model="step.config.customPattern" class="dtb-input" placeholder="e.g. 0,0.00 or {value}%" @input="onPayloadChange" />
                  <p class="dtb-hint">Use standard format tokens. <code>{'{'}value{'}'}</code> inserts the raw value.</p>
                  <p v-if="submitted && !step.config.customPattern" class="dtb-error">Required.</p>
                </div>
              </template>

              <!-- JOIN -->
              <template v-if="step.step === 'Join'">
                <div class="flex gap-2 mb-2 flex-col sm:flex-row">
                  <div class="flex-1 min-w-0">
                    <label class="dtb-field-label">Right Table Variable <span class="text-alert">*</span></label>
                    <select v-model="step.config.rightVariable" class="dtb-select" @change="onPayloadChange">
                      <option value="" disabled>Select variable…</option>
                      <option v-for="v in variables" :key="'join-' + v.value" :value="v.value">{{ v.value }}</option>
                    </select>
                    <p v-if="submitted && !step.config.rightVariable" class="dtb-error">Required.</p>
                  </div>
                  <div class="flex-1 min-w-0">
                    <label class="dtb-field-label">Join Type <span class="text-alert">*</span></label>
                    <select v-model="step.config.joinType" class="dtb-select" @change="onPayloadChange">
                      <option value="inner">Inner – only matching rows</option>
                      <option value="left">Left – all from left, matches from right</option>
                      <option value="right">Right – all from right, matches from left</option>
                      <option value="outer">Outer – all rows from both tables</option>
                    </select>
                  </div>
                </div>
                <div class="flex gap-2 mb-2 flex-col sm:flex-row">
                  <div class="flex-1 min-w-0">
                    <label class="dtb-field-label">Left Key Column <span class="text-alert">*</span></label>
                    <input v-model="step.config.leftKey" class="dtb-input" placeholder="Column from input table" @input="onPayloadChange" />
                    <p v-if="submitted && !step.config.leftKey" class="dtb-error">Required.</p>
                  </div>
                  <div class="flex-1 min-w-0">
                    <label class="dtb-field-label">Right Key Column <span class="text-alert">*</span></label>
                    <input v-model="step.config.rightKey" class="dtb-input" placeholder="Column from right table" @input="onPayloadChange" />
                    <p v-if="submitted && !step.config.rightKey" class="dtb-error">Required.</p>
                  </div>
                </div>
              </template>

              <!-- ROW COUNT -->
              <template v-if="step.step === 'RowCount'">
                <div class="mb-2">
                  <label class="dtb-field-label">Output Column Name</label>
                  <input v-model="step.config.outputColumn" class="dtb-input" placeholder="RowCount (default)" @input="onPayloadChange" />
                  <p class="dtb-hint">Returns a single-row table with the count of rows.</p>
                </div>
              </template>

              <!-- SELECT CELL -->
              <template v-if="step.step === 'SelectCell'">
                <div class="flex gap-2 mb-2 flex-col sm:flex-row">
                  <div class="flex-1 min-w-0">
                    <label class="dtb-field-label">Row Index <span class="text-alert">*</span></label>
                    <input v-model.number="step.config.rowIndex" type="number" min="0" class="dtb-input" placeholder="0 (first row)" @input="onPayloadChange" />
                    <p v-if="submitted && (step.config.rowIndex === '' || step.config.rowIndex === undefined)" class="dtb-error">Required.</p>
                  </div>
                  <div class="flex-1 min-w-0">
                    <label class="dtb-field-label">Column Name <span class="text-alert">*</span></label>
                    <input v-model="step.config.columnName" class="dtb-input" placeholder="e.g. Revenue" @input="onPayloadChange" />
                    <p v-if="submitted && !step.config.columnName" class="dtb-error">Required.</p>
                  </div>
                </div>
                <p class="dtb-hint">Returns a single value. The output variable will hold this value rather than a DataTable.</p>
              </template>

              <!-- SELECT ROW AT INDEX -->
              <template v-if="step.step === 'SelectRowAtIndex'">
                <div class="mb-2">
                  <label class="dtb-field-label">Row Index <span class="text-alert">*</span></label>
                  <input v-model.number="step.config.rowIndex" type="number" min="0" class="dtb-input" placeholder="0 (first row)" @input="onPayloadChange" />
                  <p v-if="submitted && (step.config.rowIndex === '' || step.config.rowIndex === undefined)" class="dtb-error">Required.</p>
                  <p class="dtb-hint">Returns a single-row DataTable at the given 0-based index.</p>
                </div>
              </template>

              <!-- SELECT ROWS AFTER INDEX -->
              <template v-if="step.step === 'SelectRowsAfterIndex'">
                <div class="mb-2">
                  <label class="dtb-field-label">Start Index (inclusive) <span class="text-alert">*</span></label>
                  <input v-model.number="step.config.startIndex" type="number" min="0" class="dtb-input" placeholder="e.g. 5" @input="onPayloadChange" />
                  <p v-if="submitted && (step.config.startIndex === '' || step.config.startIndex === undefined)" class="dtb-error">Required.</p>
                  <p class="dtb-hint">Returns all rows starting from this index (inclusive), 0-based.</p>
                </div>
              </template>

              <!-- SELECT ROWS BEFORE INDEX -->
              <template v-if="step.step === 'SelectRowsBeforeIndex'">
                <div class="mb-2">
                  <label class="dtb-field-label">End Index (exclusive) <span class="text-alert">*</span></label>
                  <input v-model.number="step.config.endIndex" type="number" min="1" class="dtb-input" placeholder="e.g. 10" @input="onPayloadChange" />
                  <p v-if="submitted && (step.config.endIndex === '' || step.config.endIndex === undefined)" class="dtb-error">Required.</p>
                  <p class="dtb-hint">Returns all rows before this index (exclusive), 0-based.</p>
                </div>
              </template>

              <!-- FORECAST (ARIMA) -->
              <template v-if="step.step === 'Forecast'">
                <div class="flex gap-2 mb-2 flex-col sm:flex-row">
                  <div class="flex-1 min-w-0">
                    <label class="dtb-field-label">Date Column <span class="text-alert">*</span></label>
                    <input v-model="step.config.dateColumn" class="dtb-input" placeholder="e.g. Month" @input="onPayloadChange" />
                    <p v-if="submitted && !step.config.dateColumn" class="dtb-error">Required.</p>
                  </div>
                  <div class="flex-1 min-w-0">
                    <label class="dtb-field-label">Value Column <span class="text-alert">*</span></label>
                    <input v-model="step.config.valueColumn" class="dtb-input" placeholder="e.g. Revenue" @input="onPayloadChange" />
                    <p v-if="submitted && !step.config.valueColumn" class="dtb-error">Required.</p>
                  </div>
                </div>
                <div class="flex gap-2 mb-2 flex-col sm:flex-row">
                  <div class="flex-1 min-w-0">
                    <label class="dtb-field-label">Time Bucket <span class="text-alert">*</span></label>
                    <select v-model="step.config.timeBucket" class="dtb-select" @change="onPayloadChange">
                      <option value="Monthly">Monthly</option>
                      <option value="Quarterly">Quarterly</option>
                      <option value="Yearly">Yearly</option>
                    </select>
                  </div>
                  <div class="flex-1 min-w-0">
                    <label class="dtb-field-label">Forecast Periods</label>
                    <input v-model.number="step.config.forecastPeriods" type="number" min="1" class="dtb-input" placeholder="Auto (based on data length)" @input="onPayloadChange" />
                    <p class="dtb-hint">Leave blank for auto: 3 for ≤12 rows, 4 for ≤24, 6 otherwise.</p>
                  </div>
                </div>
                <div class="flex gap-2 mb-2 flex-col sm:flex-row">
                  <div class="flex-1 min-w-0">
                    <label class="dtb-field-label">ARIMA p (autoregressive order)</label>
                    <input v-model.number="step.config.p" type="number" min="0" class="dtb-input" placeholder="1 (default)" @input="onPayloadChange" />
                  </div>
                  <div class="flex-1 min-w-0">
                    <label class="dtb-field-label">ARIMA d (differencing)</label>
                    <input v-model.number="step.config.d" type="number" min="0" class="dtb-input" placeholder="0 (default)" @input="onPayloadChange" />
                  </div>
                  <div class="flex-1 min-w-0">
                    <label class="dtb-field-label">ARIMA q (moving average order)</label>
                    <input v-model.number="step.config.q" type="number" min="0" class="dtb-input" placeholder="1 (default)" @input="onPayloadChange" />
                  </div>
                </div>
                <p class="dtb-hint">Appends predicted rows to the dataset with a "Forecast" column ("Yes"/"No"). Date column is auto-extended based on the time bucket.</p>
              </template>
            </div>
          </div>
        </transition-group>
      </div>

      <!-- ─── JSON Preview (collapsible) ─── -->
      <div class="px-4 py-3 border-x border-b border-stroke rounded-b-lg">
        <button type="button" class="flex items-center gap-2 bg-transparent border-none cursor-pointer text-xs font-semibold text-gray-500 p-0" @click="showPreview = !showPreview">
          <svg :class="['w-4 h-4 transition-transform duration-200', showPreview ? 'rotate-90' : '']" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd" />
          </svg>
          Pipeline Payload Preview
        </button>
        <pre v-if="showPreview" class="mt-2 p-3 bg-surface-dark border border-stroke rounded-md text-[11px] font-mono overflow-x-auto whitespace-pre leading-relaxed max-h-[400px] overflow-y-auto">{{
          payloadJson
        }}</pre>
      </div>

      <!-- ─── Output Variable ─── -->
      <VariableSelect label="Output Variable" v-model="outputVariable" class="mt-3 w-full" system :filter-data-type="['DataTable']" @update:modelValue="onPayloadChange" />
      <p v-if="submitted && !outputVariable" class="mt-1 text-[11px] text-alert font-medium">Output variable is required.</p>
    </template>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import uuidv4 from '../../../helpers/uuid';
import EvaluateItemsConfig from './EvaluateItemsConfig';
import TagInput from './data-transform/TagInput.vue';
import TransformFields from './data-transform/TransformFields.vue';

/* ───────────────────────────────────────────
   TransformFields sub-component
   Renders the config inputs for a selected transform.
   Used by both UpdateColumn and AddColumn.
   ─────────────────────────────────────────── */

/* ───────────────────────────────────────────
   Helpers
   ─────────────────────────────────────────── */
let stepIdCounter = 0;
function nextId() {
  return ++stepIdCounter;
}

function defaultConfigForStep(stepName) {
  const transformBase = {
    transform: '',
    lookupList: {},
    defaultValue: '',
    template: '',
    left: '',
    right: '',
    searchValue: '',
    replaceValue: '',
    start: 0,
    end: undefined,
    delimiter: '',
    index: 0,
    columns: [],
    separator: '',
    operation: '',
    value: '',
    trueValue: '',
    falseValue: '',
    decimals: 0,
  };
  switch (stepName) {
    case 'Filter':
      return { columnName: '', operation: '', value: '' };
    case 'SelectColumns':
      return { columns: [] };
    case 'UpdateColumn':
      return { columnName: '', ...transformBase };
    case 'AddColumn':
      return { newColumnName: '', columnName: '', ...transformBase };
    case 'RemoveColumn':
      return { columns: [] };
    case 'RenameColumn':
      return { columnName: '', newColumnName: '' };
    case 'RenameColumns':
      return { mapping: {} };
    case 'GroupByAndCount':
      return { groupColumns: [], countColumn: '' };
    case 'GroupByAndSum':
      return { groupColumns: [], sumColumns: [] };
    case 'GroupByAndAverage':
      return { groupColumns: [], averageColumns: [] };
    case 'GroupByAndMin':
      return { groupColumns: [], minColumns: [] };
    case 'GroupByAndMax':
      return { groupColumns: [], maxColumns: [] };
    case 'SortBy':
      return { sortColumns: [{ columnName: '', direction: 'asc' }] };
    case 'Deduplicate':
      return { columns: [] };
    case 'Limit':
      return { count: 100 };
    case 'FillMissing':
      return { columnName: '', fillValue: '' };
    case 'Pivot':
      return { groupColumns: [], pivotColumn: '', valueColumn: '', aggregation: 'sum' };
    case 'Unpivot':
      return { idColumns: [], unpivotColumns: [], keyColumn: 'Variable', valueColumn: 'Value' };
    // New steps
    case 'Average':
      return { suffix: '_avg' };
    case 'Summarize':
      return { labelColumn: '', labelValue: 'Total' };
    case 'Format':
      return { columnName: '', formatType: '', decimals: 2, currencySymbol: '$', thousandsSeparator: ',', dateFormat: 'YYYY-MM-DD', customPattern: '' };
    case 'Join':
      return { rightVariable: '', joinType: 'inner', leftKey: '', rightKey: '' };
    case 'RowCount':
      return { outputColumn: 'RowCount' };
    case 'SelectCell':
      return { rowIndex: 0, columnName: '' };
    case 'SelectRowAtIndex':
      return { rowIndex: 0 };
    case 'SelectRowsAfterIndex':
      return { startIndex: 0 };
    case 'SelectRowsBeforeIndex':
      return { endIndex: 10 };
    case 'Forecast':
      return { dateColumn: '', valueColumn: '', timeBucket: 'Monthly', forecastPeriods: null, p: 1, d: 0, q: 1 };
    default:
      return {};
  }
}

function cleanConfig(stepName, config) {
  const clean = {};
  Object.keys(config).forEach((k) => {
    if (k.startsWith('_')) return;
    const val = config[k];
    if (val === undefined) return;
    clean[k] = val;
  });

  if (stepName === 'AddColumn' || stepName === 'UpdateColumn') {
    const t = clean.transform;
    if (!t) return clean;

    const keep = new Set(['transform']);
    if (stepName === 'AddColumn') keep.add('newColumnName');
    if (stepName === 'UpdateColumn') keep.add('columnName');

    const columnNameTransforms = [
      'year',
      'month',
      'month(mmm)',
      'quarter',
      'quarter(Qx)',
      'yearMonth',
      'yearQuarter',
      'date',
      'dayOfWeek',
      'number',
      'decimal',
      'trim',
      'lowercase',
      'uppercase',
      'absoluteValue',
      'round',
      'lookup',
      'conditional',
    ];
    if (columnNameTransforms.includes(t)) keep.add('columnName');

    if (t === 'lookup') {
      keep.add('lookupList');
      keep.add('defaultValue');
    }
    if (t === 'template') keep.add('template');
    if (['multiply', 'divide', 'add', 'subtract', 'percentage', 'daysBetween'].includes(t)) {
      keep.add('left');
      keep.add('right');
    }
    if (t === 'replace') {
      keep.add('columnName');
      keep.add('searchValue');
      keep.add('replaceValue');
    }
    if (t === 'substring') {
      keep.add('columnName');
      keep.add('start');
      keep.add('end');
    }
    if (t === 'splitPart') {
      keep.add('columnName');
      keep.add('delimiter');
      keep.add('index');
    }
    if (['concat', 'coalesce'].includes(t)) {
      keep.add('columns');
      if (t === 'concat') keep.add('separator');
    }
    if (t === 'conditional') {
      keep.add('operation');
      keep.add('value');
      keep.add('trueValue');
      keep.add('falseValue');
    }
    if (t === 'round') keep.add('decimals');
    if (t === 'constant') keep.add('value');

    const result = {};
    keep.forEach((k) => {
      if (clean[k] !== undefined) result[k] = clean[k];
    });
    return result;
  }

  if (stepName === 'Filter') {
    if (clean.value !== '' && clean.value !== undefined && !Number.isNaN(Number(clean.value))) {
      clean.value = Number(clean.value);
    }
  }

  // Strip nulls/empty from Forecast
  if (stepName === 'Forecast') {
    if (clean.forecastPeriods === null || clean.forecastPeriods === '') delete clean.forecastPeriods;
  }

  return clean;
}

/* ───────────────────────────────────────────
   Main component
   ─────────────────────────────────────────── */
export default {
  name: 'DataTransformConfig',
  components: { TagInput, TransformFields },
  props: {
    item: { type: Object, default: null },
    isAgent: { type: Boolean, default: false },
  },
  data() {
    let init;
    if (this.item) {
      init = JSON.parse(JSON.stringify(this.item.payload));
    } else {
      init = structuredClone(EvaluateItemsConfig['data-transform'].payload);
    }
    return {
      inputVariable: init?.inputVariable || '',
      outputVariable: init?.outputVariable || '',
      title: init?.title || '',
      steps: init?.steps
        ? init.steps.map((s) => ({
            id: nextId(),
            colInput: '',
            sumInput: '',
            avgInput: '',
            minInput: '',
            maxInput: '',
            unpivotInput: '',
            renameEntries: s.step === 'RenameColumns' ? Object.entries(s.config.mapping || {}).map(([o, n]) => ({ oldName: o, newName: n })) : [],
            step: s.step,
            config: { ...defaultConfigForStep(s.step), ...s.config },
          }))
        : [],
      submitted: false,
      showPreview: false,

      stepOptionGroups: [
        {
          label: 'Filtering & Selection',
          options: [
            { value: 'Filter', label: 'Filter' },
            { value: 'SelectColumns', label: 'Select Columns' },
            { value: 'RemoveColumn', label: 'Remove Column(s)' },
            { value: 'Deduplicate', label: 'Deduplicate' },
            { value: 'Limit', label: 'Limit Rows' },
            { value: 'SelectRowAtIndex', label: 'Select Row at Index' },
            { value: 'SelectRowsAfterIndex', label: 'Select Rows After Index' },
            { value: 'SelectRowsBeforeIndex', label: 'Select Rows Before Index' },
            { value: 'SelectCell', label: 'Select Cell Value' },
          ],
        },
        {
          label: 'Column Operations',
          options: [
            { value: 'AddColumn', label: 'Add Column' },
            { value: 'UpdateColumn', label: 'Update Column' },
            { value: 'RenameColumn', label: 'Rename Column' },
            { value: 'RenameColumns', label: 'Rename Columns (batch)' },
            { value: 'FillMissing', label: 'Fill Missing Values' },
            { value: 'Format', label: 'Format Column' },
          ],
        },
        {
          label: 'Aggregation & Grouping',
          options: [
            { value: 'GroupByAndCount', label: 'Group By & Count' },
            { value: 'GroupByAndSum', label: 'Group By & Sum' },
            { value: 'GroupByAndAverage', label: 'Group By & Average' },
            { value: 'GroupByAndMin', label: 'Group By & Min' },
            { value: 'GroupByAndMax', label: 'Group By & Max' },
            { value: 'Average', label: 'Average (all numeric)' },
            { value: 'Summarize', label: 'Summarize (totals)' },
            { value: 'RowCount', label: 'Row Count' },
          ],
        },
        {
          label: 'Sorting & Reshaping',
          options: [
            { value: 'SortBy', label: 'Sort By' },
            { value: 'Pivot', label: 'Pivot (wide format)' },
            { value: 'Unpivot', label: 'Unpivot (long format)' },
          ],
        },
        {
          label: 'Joins & Combining',
          options: [{ value: 'Join', label: 'Join Tables' }],
        },
        {
          label: 'Analytics & Forecasting',
          options: [{ value: 'Forecast', label: 'Forecast (ARIMA)' }],
        },
      ],

      transformOptionGroups: [
        {
          label: 'Date Transforms',
          options: [
            { value: 'year', label: 'year — Extract 4-digit year' },
            { value: 'month', label: 'month — Extract month number (1-12)' },
            { value: 'month(mmm)', label: 'month(mmm) — Month abbreviation (Jan, Feb…)' },
            { value: 'quarter', label: 'quarter — Quarter number (1-4)' },
            { value: 'quarter(Qx)', label: 'quarter(Qx) — Quarter label (Q1, Q2…)' },
            { value: 'yearMonth', label: 'yearMonth — e.g. 2024-01' },
            { value: 'yearQuarter', label: 'yearQuarter — e.g. 2024-Q1' },
            { value: 'date', label: 'date — Parse as date object' },
            { value: 'dayOfWeek', label: 'dayOfWeek — Day name (Monday…)' },
            { value: 'daysBetween', label: 'daysBetween — Days between two date columns' },
          ],
        },
        {
          label: 'Text Transforms',
          options: [
            { value: 'template', label: 'template — Build string from column values' },
            { value: 'trim', label: 'trim — Remove leading/trailing whitespace' },
            { value: 'lowercase', label: 'lowercase — Convert to lowercase' },
            { value: 'uppercase', label: 'uppercase — Convert to UPPERCASE' },
            { value: 'replace', label: 'replace — Find & replace text' },
            { value: 'substring', label: 'substring — Extract part of text by position' },
            { value: 'splitPart', label: 'splitPart — Split by delimiter, take nth part' },
            { value: 'concat', label: 'concat — Join multiple columns into one string' },
          ],
        },
        {
          label: 'Numeric Transforms',
          options: [
            { value: 'number', label: 'number — Cast to integer' },
            { value: 'decimal', label: 'decimal — Cast to decimal (float)' },
            { value: 'multiply', label: 'multiply — Multiply two columns' },
            { value: 'divide', label: 'divide — Divide left column by right' },
            { value: 'add', label: 'add — Sum two columns' },
            { value: 'subtract', label: 'subtract — Left column minus right' },
            { value: 'percentage', label: 'percentage — (left / right) × 100' },
            { value: 'round', label: 'round — Round to N decimal places' },
            { value: 'absoluteValue', label: 'absoluteValue — Remove negative sign' },
          ],
        },
        {
          label: 'Logic & Mapping',
          options: [
            { value: 'lookup', label: 'lookup — Map values via key→value list' },
            { value: 'coalesce', label: 'coalesce — First non-empty value from columns' },
            { value: 'conditional', label: 'conditional — If/else based on a condition' },
            { value: 'constant', label: 'constant — Set a fixed value for all rows' },
          ],
        },
      ],

      operatorOptions: [
        { value: '===', label: 'Equals (===)' },
        { value: '!==', label: 'Not Equals (!==)' },
        { value: '>', label: 'Greater Than (>)' },
        { value: '>=', label: 'Greater or Equal (>=)' },
        { value: '<', label: 'Less Than (<)' },
        { value: '<=', label: 'Less or Equal (<=)' },
        { value: 'startsWith', label: 'Starts With' },
        { value: 'endsWith', label: 'Ends With' },
        { value: 'contains', label: 'Contains' },
        { value: 'isEmpty', label: 'Is Empty' },
        { value: 'isNotEmpty', label: 'Is Not Empty' },
      ],

      stepRegistry: {
        Filter: { description: 'Keep only rows matching a condition.' },
        SelectColumns: { description: 'Retain only the listed columns.' },
        UpdateColumn: { description: 'Overwrite values in an existing column using a transform.' },
        AddColumn: { description: 'Create a new column from existing data using a transform.' },
        RemoveColumn: { description: 'Drop one or more columns from the dataset.' },
        RenameColumn: { description: 'Rename a single column.' },
        RenameColumns: { description: 'Rename multiple columns at once.' },
        GroupByAndCount: { description: 'Group rows and count occurrences per group.' },
        GroupByAndSum: { description: 'Group rows and sum numeric columns per group.' },
        GroupByAndAverage: { description: 'Group rows and average numeric columns per group.' },
        GroupByAndMin: { description: 'Group rows and find the minimum of numeric columns.' },
        GroupByAndMax: { description: 'Group rows and find the maximum of numeric columns.' },
        SortBy: { description: 'Sort rows by one or more columns.' },
        Deduplicate: { description: 'Remove duplicate rows based on specified columns.' },
        Limit: { description: 'Return only the first N rows.' },
        FillMissing: { description: 'Replace empty/null values with a default.' },
        Pivot: { description: 'Pivot rows into columns (wide format).' },
        Unpivot: { description: 'Convert columns into rows (long format).' },
        // New
        Average: { description: 'Calculate the average for all numeric columns, returning a single summary row.' },
        Summarize: { description: 'Calculate totals (sum) for all numeric columns, returning a single summary row.' },
        Format: { description: 'Format column values as number, currency, percent, date, or custom pattern.' },
        Join: { description: 'Join this table with another data table on matching key columns.' },
        RowCount: { description: 'Return a single-row table with the number of rows in the dataset.' },
        SelectCell: { description: 'Retrieve a specific cell value by row index and column name.' },
        SelectRowAtIndex: { description: 'Select a single row at the given 0-based index.' },
        SelectRowsAfterIndex: { description: 'Select all rows starting from the given index (inclusive).' },
        SelectRowsBeforeIndex: { description: 'Select all rows before the given index (exclusive).' },
        Forecast: { description: 'Forecast future values using ARIMA time series analysis. Appends predicted rows with a "Forecast" indicator column.' },
      },
      payloadJson: '',
    };
  },
  computed: {
    ...mapState(['projectData']),
    variables() {
      let variables = [];
      if (this.projectId && Object.keys(this.projectData).length) {
        variables = Object.keys(this.projectData?.[this.projectId]?.variables || {}).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase())) || [];
      }
      return variables.map((variable) => ({ id: uuidv4(), value: variable }));
    },
    projectId() {
      return this.$route.params.projectId;
    },
    payload() {
      return {
        id: this.item.id,
        type: this.item.type,
        payload: this.item.payload,
      };
    },
    // payloadJson() {
    //   console.log('payloadJson', this.payload); // eslint-disable-line
    //   return JSON.stringify(this.payload, null, 2);
    // },
  },
  methods: {
    ...mapActions(['showToastMessage']),
    addStep() {
      this.steps.push({
        id: nextId(),
        colInput: '',
        sumInput: '',
        avgInput: '',
        minInput: '',
        maxInput: '',
        unpivotInput: '',
        renameEntries: [],
        step: '',
        config: {},
      });
      this.onPayloadChange();
    },
    removeStep(idx) {
      this.steps.splice(idx, 1);
      this.onPayloadChange();
    },
    duplicateStep(idx) {
      const src = this.steps[idx];
      const dup = JSON.parse(JSON.stringify(src));
      dup.id = nextId();
      this.steps.splice(idx + 1, 0, dup);
      this.onPayloadChange();
    },
    moveStep(idx, direction) {
      const target = idx + direction;
      if (target < 0 || target >= this.steps.length) return;
      const temp = this.steps.splice(idx, 1)[0];
      this.steps.splice(target, 0, temp);
      this.onPayloadChange();
    },
    onStepTypeChange(idx) {
      const step = this.steps[idx];
      step.config = defaultConfigForStep(step.step);
      step.colInput = '';
      step.sumInput = '';
      step.avgInput = '';
      step.minInput = '';
      step.maxInput = '';
      step.unpivotInput = '';
      step.renameEntries = step.step === 'RenameColumns' ? [{ oldName: '', newName: '' }] : [];
      this.onPayloadChange();
    },
    onTransformChange(step) {
      const preserve = {};
      if (step.config.columnName) preserve.columnName = step.config.columnName;
      if (step.config.newColumnName) preserve.newColumnName = step.config.newColumnName;
      const currentTransform = step.config.transform;
      const base = defaultConfigForStep(step.step);
      Object.assign(step.config, base, preserve);
      step.config.transform = currentTransform;
      this.onPayloadChange();
    },
    addTag(step, configKey) {
      const val = (step.colInput || '').trim();
      if (!val) return;
      if (!step.config[configKey]) this.$set(step.config, configKey, []);
      step.config[configKey].push(val);
      step.colInput = '';
      this.onPayloadChange();
    },
    addTagCustom(step, inputKey, configKey) {
      const val = (step[inputKey] || '').trim();
      if (!val) return;
      if (!step.config[configKey]) this.$set(step.config, configKey, []);
      step.config[configKey].push(val);
      step[inputKey] = '';
      this.onPayloadChange();
    },
    removeTag(arr, idx) {
      arr.splice(idx, 1);
      this.onPayloadChange();
    },
    syncRenameMapping(step) {
      const mapping = {};
      (step.renameEntries || []).forEach((e) => {
        if (e.oldName.trim()) mapping[e.oldName.trim()] = e.newName;
      });
      step.config.mapping = mapping;
      this.onPayloadChange();
    },
    needsColumnName(transform) {
      return [
        'year',
        'month',
        'month(mmm)',
        'quarter',
        'quarter(Qx)',
        'yearMonth',
        'yearQuarter',
        'date',
        'dayOfWeek',
        'number',
        'decimal',
        'trim',
        'lowercase',
        'uppercase',
        'absoluteValue',
        'round',
        'lookup',
        'replace',
        'substring',
        'splitPart',
        'conditional',
      ].includes(transform);
    },
    onPayloadChange() {
      this.payload.payload = JSON.parse(
        JSON.stringify({
          inputVariable: this.inputVariable,
          outputVariable: this.outputVariable,
          title: this.title,
          steps: this.steps
            .filter((s) => s.step)
            .map((s) => ({
              step: s.step,
              config: cleanConfig(s.step, s.config),
            })),
        }),
      );
      this.payloadJson = JSON.stringify(this.payload, null, 2);
      console.log('steps', this.steps); // eslint-disable-line
      console.log('onPayloadChange', this.payload); // eslint-disable-line
      this.$nextTick(() => {
        this.submitted = true;
        this.$emit('update', this.payload);
      });
    },
  },
};
</script>

<style scoped>
/* eslint-disable max-len */
/* ─── Minimal custom styles that can't be done with Tailwind alone ─── */
.dtb-select {
  @apply block w-full py-2 px-2 text-[13px] border border-stroke rounded-md bg-white text-gray-800 outline-none transition-all duration-150 box-border;
  appearance: auto;
}
.dtb-select:focus {
  @apply border-secondary ring-2 ring-secondary/[0.18];
}
.dtb-input {
  @apply block w-full py-[7px] px-2 text-[13px] border border-stroke rounded-md bg-white text-gray-800 outline-none transition-all duration-150 box-border;
}
.dtb-input:focus {
  @apply border-secondary ring-2 ring-secondary/[0.18];
}
.dtb-field-label {
  @apply block font-400 text-xs text-gray-500 mb-[3px];
}
.dtb-error {
  @apply mt-1 text-[11px] text-alert font-400;
}
.dtb-hint {
  @apply mt-1 text-[11px] text-gray-500;
}
.dtb-icon-btn {
  @apply inline-flex items-center justify-center w-7 h-7 border-none rounded-md bg-transparent text-gray-500 cursor-pointer transition-all duration-150 p-0 shrink-0;
}
.dtb-icon-btn svg {
  @apply w-[15px] h-[15px];
}
.dtb-icon-btn:hover {
  @apply bg-surface-dark;
}
.dtb-icon-btn:disabled {
  @apply opacity-30 cursor-not-allowed;
}
.dtb-btn-sm {
  @apply inline-flex items-center gap-1 py-1 px-2 text-[11px] font-500 rounded-md bg-surface-dark text-gray-500 border border-stroke cursor-pointer transition-colors duration-150 hover:bg-off-white;
}

/* Transition */
.dtb-step-anim-enter-active,
.dtb-step-anim-leave-active {
  transition: all 0.25s ease;
}
.dtb-step-anim-enter,
.dtb-step-anim-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
</style>
